/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 8/7/21.
 */

import * as _ from "lodash";
import AssetApi from "@/services/Asset";
import moment from "moment-timezone";
import {uuid} from "vue-uuid";

export default {
	state: () => ({
		assetTypes: [],
		assetsHotkeysSubscriptionsMap: {}
	}),

	getters: {
		getAssetTypes: (state) => () => {
			return state.assetTypes;
		},

		getAssetTypeById: (state) => (assetTypeId) => {
			return state.assetTypes.find(t => t.AssetTypeID === assetTypeId);
		},

		getAssetTypeColor: (state) => (assetTypeId) => {
			return state.assetTypes.find(t => t.AssetTypeID === assetTypeId)?.Color;
		},

		getAssetTypeIcon: (state) => (assetTypeId) => {
			let icon = state.assetTypes.find(t => t.AssetTypeID === assetTypeId)?.Icon;

			switch (icon) {
				case "dollar sign":
					return "currency-usd";
				case "linkify":
					return "link";
				case "comments outline":
					return "comment-multiple-outline";
				case "birthday cake":
					return "cake";
				case "file outline":
					return "file-outline";
				case "file audio":
					return "file-music-outline";
				case "file video":
					return "file-video-outline";
				case "sun":
					return "white-balance-sunny";
				case "globe":
					return "earth";
				case "smile":
					return "emoticon-happy";
				case "frown":
					return "emoticon-frown";
				case "recycle retweet":
					return "recycle";
				case "user secret":
					return "incognito";
				case "thumbs down":
					return "thumb-down";
				case "thumbtack":
					return "pin";
				case "tint":
					return "droplet";
				default:
					return icon;
			}
		},

		getHotkeysByAssetID: (state) => (assetID) => {
			return state.assetsHotkeysSubscriptionsMap[assetID] ?? [];
		},

		isSubscribedToAssetTopics: (state) => (assetID) => {
			return !!state.assetsHotkeysSubscriptionsMap[assetID];
		},
	},

	mutations: {
		setAssetTypes(state, assetTypes) {
			state.assetTypes = assetTypes;
		},

		addAssetHotkeySubscription(state, {assetID, hotkey}) {
			if (!state.assetsHotkeysSubscriptionsMap[assetID]) {
				state.assetsHotkeysSubscriptionsMap[assetID] = [hotkey];
			}
			else if (!state.assetsHotkeysSubscriptionsMap[assetID].find(({EventID}) => EventID === hotkey.EventID)) {
				state.assetsHotkeysSubscriptionsMap[assetID].push(hotkey);
			}
		},

		removeAssetHotkeySubscription(state, {assetID, eventID}) {
			if (state.assetsHotkeysSubscriptionsMap[assetID]) {
				if (eventID) {
					let index = state.assetsHotkeysSubscriptionsMap[assetID].findIndex(
						({EventID}) => EventID === eventID
					);

					if (index > -1) {
						state.assetsHotkeysSubscriptionsMap[assetID].splice(index, 1);
					}

					// if there are not any events left remove the assetID from the map
					if (!state.assetsHotkeysSubscriptionsMap[assetID].length) {
						delete state.assetsHotkeysSubscriptionsMap[assetID];
					}
				}
				else {
					delete state.assetsHotkeysSubscriptionsMap[assetID];
				}
			}
		},

		updateAssetHotkeySubscription(state, updatedHotkey) {
			let assetsHotkeysSubscriptions = state.assetsHotkeysSubscriptionsMap[updatedHotkey.AssetID];
			let hotkey = assetsHotkeysSubscriptions?.find(({EventID}) => EventID === updatedHotkey.EventID);
			if (hotkey && assetsHotkeysSubscriptions) {
				assetsHotkeysSubscriptions.splice(assetsHotkeysSubscriptions.indexOf(hotkey), 1, updatedHotkey);
			}
		},

		resetAssetsHotkeysSubscriptions(state) {
			state.assetsHotkeysSubscriptionsMap = {};
		},

		setAssetLastAccessed(state, {assetTypeID, lastAccessed}) {
			if (assetTypeID) {
				state.assetTypes.find(a => a.AssetTypeID === assetTypeID).LastAccessed = lastAccessed;
			}
			else {
				state.assetTypes.forEach(a => {
					a.LastAccessed = lastAccessed;
				});
			}
		},

		sortAssetsByNumeric(state, sortBy) {
			state.assetTypes = _.orderBy(state.assetTypes, sortBy, "desc");
		}
	},

	actions: {
		async getAssetTypes({commit}) {
			return await AssetApi.getTypes()
			                     .then(response => {
									 commit("setAssetTypes", response.data);
									 return response.data;
								 })
			                     .catch(error => {
									 console.log(error);
								 });
		},

		getAsset(state, assetId) {
			return AssetApi.get(assetId)
			               .then(response => response.data)
			               .catch(error => {
							   console.log(error);
							   return null;
						   });
		},

		deleteAsset(state, assetId) {
			return AssetApi.delete(assetId);
		},

		updateAsset({commit}, payload) {
			return AssetApi.updateAsset(payload)
			               .then(response => {
							   if (payload.AssetID && payload.POIMarkup) {
								   commit("updateAssetPOIMarkupInEvents", {
									   AssetID: payload.AssetID,
									   POIMarkup: payload.POIMarkup
								   });
							   }
							   return response.data;
						   })
			               .catch(error => {
							   console.log(error);
							   return null;
						   });
		},

		getAssetAudioUrl(state, payload) {
			return AssetApi.getAudioUrl(payload.AssetID);
		},

		getAssetPartialAudio(state, payload) {
			const start = `@0.0`;
			const end = `@${payload.POIMarkup?.Length ?? 0}`;
			return AssetApi.getPartialAudio(payload.AssetID, start, end);
		},

		assetMessage({getters, commit, dispatch}, msg) {
			const operationType = msg.operationType;
			msg = _.omit(msg, ["coll", "db", "operationType", "time", "removedFields"]);

			const hotkeys = getters.getHotkeysByAssetID(msg.AssetID);
			switch (operationType) {
				case "Add":
					hotkeys.forEach(hotkey => {
						dispatch("updateHotkeyDueToMessage", {hotkey, updatedData: msg});
					});
					break;
				case "Modify":
					hotkeys.forEach(hotkey => {
						// merge the nested properties as well
						const updatedData = _.merge(hotkey.Asset, msg);
						dispatch("updateHotkeyDueToMessage", {hotkey, updatedData});
					});
					break;
				case "Delete":
					hotkeys.forEach(hotkey => {
						dispatch("updateHotkeyDueToMessage", {hotkey, updatedData: {Asset: {loading: false}}});

						commit("removeAssetHotkeySubscription", {assetID: msg.AssetID});
						dispatch("assetSubscription", {assetID: msg.AssetID, subscribe: false});
						dispatch(
							"stationAssetSubscription",
							{assetID: msg.AssetID, StationID: hotkey.StationID, subscribe: false}
						);
					});
					break;
			}
		},

		stationAssetMessage({getters, dispatch}, msg) {
			const operationType = msg.operationType;
			msg = _.omit(msg, ["coll", "db", "operationType", "time", "removedFields"]);

			if (operationType === "Modify") {
				const hotkeys = getters.getHotkeysByAssetID(msg.AssetID);

				hotkeys.forEach(hotkey => {
					// merge the nested properties as well
					const updatedData = _.merge(hotkey.Asset, msg);
					dispatch("updateHotkeyDueToMessage", {hotkey, updatedData});
				});
			}
		},

		updateAssetsLastAccessed({getters, commit}, assetTypeID) {
			if (assetTypeID === "ALL") {
				commit("sortAssetsByNumeric", "LastAccessed");
			}
			else if (assetTypeID) {
				commit("setAssetLastAccessed", {assetTypeID: assetTypeID, lastAccessed: moment.now()});
				commit("sortAssetsByNumeric", "LastAccessed");

				let assetTypes = getters.getAssetTypes().map(asset => {
					return {
						"AssetTypeID": asset.AssetTypeID,
						"LastAccessed": asset.LastAccessed
					}
				});

				localStorage.setItem("assetTypes", JSON.stringify(assetTypes));
			}
			else {
				const m = moment.now();
				commit("setAssetLastAccessed", {assetTypeID: null, lastAccessed: m});
			}
		},

		createAsset(state, payload) {
			return AssetApi.createAsset(payload);
		},

		importAssetEvent({rootGetters}, {stationID, playlistID, assetID, assetTypeID, title, sequenceNumber}) {
			const OrganizationID = rootGetters.getOrgID();
			const AssetID = assetID ?? uuid.v1();
			const payload = {
				Events: [
					{
						Asset: {
							AssetID,
							AssetTypeID: assetTypeID,
							CreatedBy: rootGetters.getUserFullName(),
							POIMarkup: {
								Path: null,
								Length: 0,
								POIMarkers: []
							},
							StationAssets: [
								{
									Active: true,
									AssetID,
									OrganizationID,
									StationID: stationID
								}
							],
							Title: title
						},
						AssetID,
						AssetTypeID: assetTypeID,
						Chain: 1,
						EventID: uuid.v1(),
						EventType: 1,
						OrganizationID,
						PlaylistID: playlistID,
						SequenceNumber: sequenceNumber,
						Skipped: false,
						StatusCode: 1
					}
				],
				OrganizationID,
				PlaylistID: playlistID,
				StationID: stationID,
				Title: title
			}
			return AssetApi.importAssetEvent(payload)
		},

		importAsset({rootGetters}, {StationID, AssetID, AssetTypeID, Title, OriginalFileName}) {
			const OrganizationID = rootGetters.getOrgID();
			const payload = {
				AssetTypeID,
				OrganizationID,
				AssetID,
				Title,
				OriginalFileName,
				"Album": null,
				"Artist": null,
				"Voice": null,
				"DisplayArtist": null,
				"DisplayTitle": null,
				"EndDateTime": null,
				"Comment": null,
				"CreatedDateTime": moment().toISOString(),
				"CreatedBy": rootGetters.getUserFullName(),
				"KillDateTime": null,
				"ModifiedDateTime": null,
				"ReleaseDateTime": null,
				"Tags": [],
				"ThirdPartyIDs": null,
				"CustomFields": null,
				"BedType": null,
				"BypassAudioNormalization": null,
				POIMarkup: {
					Path: null,
					Length: 0,
					POIMarkers: []
				},
				StationAssets: [
					{
						Active: true,
						AssetID,
						OrganizationID,
						StationID
					}
				],
			}

			return AssetApi.importAsset(payload)
		},

		getAssetAllStations(state, AssetID) {
			return AssetApi.assetAllStations(AssetID);
		},

		getStationAsset(state, payload) {
			return AssetApi.getStationAsset(payload)
			               .then(res => {
							   if (res.status === 200) {
								   return [res.data];
							   }
							   else {
								   return null;
							   }
						   })
			               .catch(err => {
							   console.error("An error occurred when getting station asset.", err);
							   return null;
						   });
		},

		updateAssetStations(state, {AssetID, payload}) {
			return AssetApi.updateAssetStations(AssetID, payload);
		},

		updateStationAsset({commit}, payload) {
			return AssetApi.updateStationAsset(payload)
			               .then(response => {
							   if (payload.AssetID && payload.POIMarkup) {
								   commit("updateAssetPOIMarkupInEvents", {
									   AssetID: payload.AssetID,
									   POIMarkup: payload.POIMarkup
								   });
							   }
							   return response;
						   });
		},

		getAssetGSelectorCategory(state, {StationID, AssetID}) {
			return AssetApi.getAssetGSelectorCategory(StationID, AssetID);
		},

		getGSelectorCategoriesList(state, {StationID, AssetTypeID}) {
			return AssetApi.getGSelectorCategoriesList(StationID, AssetTypeID);
		}
	}
};
